import React, { useMemo } from 'react';

import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Loader } from 'components/Loader';

import { useStatistic } from '../api/getStatistic';
import { buildChartRows } from '../utils/trend';

const TREND_COLOR = '#C2621A';
const CHART_HEIGHT = 280;

const formatNumber = (value) => (value === undefined || value === null ? '' : Math.round(value * 10) / 10);

const LegendKey = ({ color, dashed, bar, label }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    {bar ? (
      <Box width={10} height={10} borderRadius="2px" bgcolor={color} />
    ) : (
      <Box width={16} borderTop={`2px ${dashed ? 'dashed' : 'solid'} ${color}`} />
    )}
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const ChartTooltip = ({ active, payload, valueLabel }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;

  return (
    <Box bgcolor="background.paper" border="1px solid" borderColor="border.main" borderRadius={1} px={1.5} py={1} boxShadow={2}>
      <Typography variant="subtitle2">{row.label}</Typography>
      {row.value !== undefined && (
        <Typography variant="body2">
          {valueLabel}: {row.value}
          {row.inProgress ? ' (in progress)' : ''}
          {row.partial ? ' (counted from 1 Oct)' : ''}
        </Typography>
      )}
      {row.trend !== undefined && (
        <Typography variant="body2" color="text.secondary">
          Trend: {formatNumber(row.trend)}
        </Typography>
      )}
      {row.value === undefined && row.forecast !== undefined && (
        <Typography variant="body2" color="text.secondary">
          Forecast: {formatNumber(row.forecast)}
        </Typography>
      )}
    </Box>
  );
};

export const StatisticsChart = ({ metric, title, countLabel, granularity, from, to, unique }) => {
  const { palette } = useTheme();
  const { data, isPending, isError, error } = useStatistic({ metric, granularity, from, to });

  const valueLabel = unique ? 'Unique visitors' : countLabel;
  const { rows, total, slope } = useMemo(
    () => buildChartRows(data?.data, data?.granularity || granularity, { unique }),
    [data, granularity, unique]
  );

  return (
    <Stack border="1px solid" borderColor="border.main" borderRadius={2} p={2} gap={1.5} minWidth={0}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        {!isPending && !isError && (
          <Typography variant="body2" color="text.secondary">
            Total: <b>{total}</b>
            {slope !== null && ` · Trend ${slope >= 0 ? '+' : ''}${formatNumber(slope)} per ${data.granularity}`}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" gap={2} flexWrap="wrap">
        <LegendKey bar color={palette.primary.main} label={valueLabel} />
        {rows.some((row) => row.inProgress || row.partial) && (
          <LegendKey bar color={palette.primary.light} label="Incomplete period" />
        )}
        {slope !== null && <LegendKey color={TREND_COLOR} label="Trend" />}
        {slope !== null && <LegendKey color={TREND_COLOR} dashed label="Forecast" />}
      </Stack>

      <Box height={CHART_HEIGHT}>
        {isPending ? (
          <Loader />
        ) : isError ? (
          <Stack height={1} alignItems="center" justifyContent="center">
            <Typography color="error">{error?.response?.data?.description || 'Failed to load statistics'}</Typography>
          </Stack>
        ) : rows.length === 0 ? (
          <Stack height={1} alignItems="center" justifyContent="center">
            <Typography color="text.secondary">No data for the selected period</Typography>
          </Stack>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid vertical={false} stroke={palette.grey[200]} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: palette.grey[300] }}
                tick={{ fontSize: 12, fill: palette.text.secondary }}
                minTickGap={8}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: palette.text.secondary }}
              />
              <Tooltip
                cursor={{ fill: palette.grey[100] }}
                content={<ChartTooltip valueLabel={valueLabel} />}
              />
              <Bar dataKey="value" maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {rows.map((row) => (
                  <Cell key={row.period} fill={row.inProgress || row.partial ? palette.primary.light : palette.primary.main} />
                ))}
              </Bar>
              <Line
                dataKey="trend"
                stroke={TREND_COLOR}
                strokeWidth={2}
                strokeLinecap="round"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="forecast"
                stroke={TREND_COLOR}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Stack>
  );
};
